// Professional Xero/QuickBooks-style PDF generator.
// Uses jsPDF + jspdf-autotable.
//
// Single function generates either a QUOTE or INVOICE depending on `kind`.
// Quotes get a "Valid until" date, invoices get a "Due date", and invoices
// optionally render BSB/account payment instructions plus a "PAID" watermark
// when status === "paid".
//
// Usage (quote):
//   const { blob, base64, filename, quoteNumber } =
//     await generateQuotePDF({ lead, lineItems, notes, totals, profile, quoteNumber });
//
// Usage (invoice — generateInvoicePDF is a thin wrapper that sets kind):
//   const { blob, base64, filename } = await generateInvoicePDF({
//     lead, lineItems, notes, totals, profile,
//     docNumber: "INV-12AB34CD",
//     payment: { bsb: "062-000", accountNumber: "1234 5678", accountName: "ACME" },
//     invoiceStatus: "paid",
//   });
//
//   // Trigger browser download (works for either):
//   downloadQuotePDF(blob, filename);

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const GST_RATE = 0.1;

// ── Brand colours / layout constants ─────────────────────────────────────────
const COLORS = {
  charcoal: [28, 28, 30],
  orange: [255, 138, 0],
  headerBand: [248, 248, 248],
  rowAlt: [249, 249, 249],
  greyBox: [245, 245, 245],
  white: [255, 255, 255],
  amber: [245, 158, 11],
  green: [34, 197, 94],
  textMuted: [110, 110, 115],
  border: [220, 220, 220],
};

const PAGE = {
  w: 210, // A4 width in mm
  h: 297, // A4 height
  margin: 15,
  topMargin: 20, // top margin on continuation pages (no header band)
  footerH: 18,   // footer band height -- content must not overlap this
};

// ── Helpers ──────────────────────────────────────────────────────────────────
function fmtMoney(n) {
  const v = Number(n) || 0;
  const abs = Math.abs(v).toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return v < 0 ? `-$${abs}` : `$${abs}`;
}

function fmtDate(d) {
  return new Date(d).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function safeName(s) {
  return String(s || "Customer")
    .replace(/[^a-zA-Z0-9_-]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

// Fetch an image URL and return a data URL we can embed in the PDF.
// Returns null on any failure (CORS, 404, etc.) so the generator can fall
// back to text-only branding.
async function fetchImageAsDataUrl(url) {
  if (!url) return null;
  try {
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function setFill(doc, rgb) {
  doc.setFillColor(rgb[0], rgb[1], rgb[2]);
}
function setText(doc, rgb) {
  doc.setTextColor(rgb[0], rgb[1], rgb[2]);
}
function setDraw(doc, rgb) {
  doc.setDrawColor(rgb[0], rgb[1], rgb[2]);
}

// ── Main generator ───────────────────────────────────────────────────────────
export async function generateQuotePDF({
  lead,
  lineItems = [],
  notes = "",
  totals = {},
  gstRegistered = true,
  profile = {},
  quoteNumber = "RQ-00000",
  acceptUrl = "",
  disclaimer = "",
  // Invoice-only options. Ignored when kind === "quote".
  kind = "quote",
  docNumber = null,
  payment = null,
  invoiceStatus = null,
  dueDate = null,
}) {
  const isInvoice = kind === "invoice";
  const docNo = docNumber || quoteNumber;

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  doc.setFont("helvetica", "normal");

  const today = new Date();
  const validUntil = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
  const computedDueDate = dueDate
    ? new Date(dueDate)
    : new Date(today.getTime() + 14 * 24 * 60 * 60 * 1000);

  // Try to embed the tradie logo — prefer logo_url (profile-photos bucket),
  // fall back to legacy avatar_url if logo_url not set yet.
  const logoDataUrl = await fetchImageAsDataUrl(profile.logo_url || profile.avatar_url);

  // ── Header band (top 28mm — tightened to free vertical room for 1-page quotes) ──
  setFill(doc, COLORS.headerBand);
  doc.rect(0, 0, PAGE.w, 28, "F");

  // Orange accent line just below band
  setFill(doc, COLORS.orange);
  doc.rect(0, 28, PAGE.w, 1.5, "F");

  // ── Left side: logo or business name ───────────────────────────────────────
  if (logoDataUrl) {
    try {
      doc.addImage(logoDataUrl, "PNG", PAGE.margin, 6, 36, 18);
    } catch {
      // Fallback to text if addImage fails (unsupported format etc.)
      drawBusinessNameText(doc, profile);
    }
  } else {
    drawBusinessNameText(doc, profile);
  }

  // ── Right side: title + meta, right-aligned ────────────────────────────────
  const rightX = PAGE.w - PAGE.margin;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  setText(doc, COLORS.charcoal);
  doc.text(isInvoice ? "INVOICE" : "QUOTE", rightX, 12, { align: "right" });

  doc.setFontSize(10);
  setText(doc, COLORS.orange);
  doc.text(docNo, rightX, 17, { align: "right" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  setText(doc, COLORS.charcoal);
  doc.text(`Date issued: ${fmtDate(today)}`, rightX, 22, { align: "right" });
  if (isInvoice) {
    doc.text(`Due date: ${fmtDate(computedDueDate)}`, rightX, 26, { align: "right" });
  } else {
    doc.text(`Valid until: ${fmtDate(validUntil)}`, rightX, 26, { align: "right" });
  }

  const status = isInvoice
    ? (invoiceStatus || "pending").toLowerCase()
    : (lead?.quote?.customer_status || "pending").toLowerCase();
  drawStatusBadge(doc, status, rightX, 30, isInvoice);

  // ── Two-column info section: Prepared by / Prepared for ────────────────────
  let cursorY = 40;
  const colW = (PAGE.w - PAGE.margin * 2) / 2 - 5;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  setText(doc, COLORS.charcoal);
  doc.text("PREPARED BY", PAGE.margin, cursorY);
  doc.text("PREPARED FOR", PAGE.margin + colW + 10, cursorY);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  setText(doc, COLORS.charcoal);

  const insuredLine =
    profile.has_insurance && profile.insurance_provider
      ? `Insured: ${profile.insurance_provider}${
          profile.insurance_policy_number
            ? ` (${profile.insurance_policy_number})`
            : ""
        }`
      : null;
  const preparedBy = [
    profile.full_name,
    profile.business_name,
    profile.abn ? `ABN: ${profile.abn}` : null,
    profile.licence_number ? `Licence: ${profile.licence_number}` : null,
    insuredLine,
    profile.phone,
    profile.email,
  ].filter(Boolean);

  const form = lead?.form || {};
  const preparedFor = [
    form.fullName || lead?.customer,
    form.address,
    form.suburb || lead?.suburb,
    form.phone,
    form.email,
  ].filter(Boolean);

  let leftY = cursorY + 4;
  preparedBy.forEach((line) => {
    doc.text(String(line), PAGE.margin, leftY);
    leftY += 4;
  });

  let rightY = cursorY + 4;
  preparedFor.forEach((line) => {
    doc.text(String(line), PAGE.margin + colW + 10, rightY);
    rightY += 4;
  });

  cursorY = Math.max(leftY, rightY) + 4;

  // ── Job details box ────────────────────────────────────────────────────────
  const jobLines = [
    form.trade && `Trade: ${form.trade}`,
    form.projectType && `Project type: ${form.projectType}`,
    form.address && `Property address: ${form.address}`,
    form.areas && `Scope / areas: ${form.areas}`,
    form.condition && `Condition: ${form.condition}`,
    form.urgency && `Urgency: ${form.urgency}`,
    form.accessDetails && `Access notes: ${form.accessDetails}`,
    form.notes && `Customer notes: ${form.notes}`,
  ].filter(Boolean);

  if (jobLines.length) {
    const wrapped = [];
    jobLines.forEach((l) => {
      doc.splitTextToSize(l, PAGE.w - PAGE.margin * 2 - 8).forEach((w) => wrapped.push(w));
    });

    const boxH = 9 + wrapped.length * 4;
    setFill(doc, COLORS.greyBox);
    doc.rect(PAGE.margin, cursorY, PAGE.w - PAGE.margin * 2, boxH, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    setText(doc, COLORS.charcoal);
    doc.text("JOB DETAILS", PAGE.margin + 4, cursorY + 5.5);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.8);
    let jy = cursorY + 10.5;
    wrapped.forEach((w) => {
      doc.text(w, PAGE.margin + 4, jy);
      jy += 4;
    });

    cursorY += boxH + 4;
  }

  // ── Line items table ───────────────────────────────────────────────────────
  const tableBody = lineItems.map((it) => [
    it.description || "",
    fmtMoney(it.amount),
  ]);

  autoTable(doc, {
    startY: cursorY,
    head: [["Description", gstRegistered ? "Amount (ex GST)" : "Amount"]],
    body: tableBody,
    // Reserve footer space so autoTable breaks rows above the band.
    margin: {
      left: PAGE.margin,
      right: PAGE.margin,
      bottom: PAGE.footerH + 4,
      top: PAGE.topMargin,
    },
    // Draw the footer band on every page autoTable emits.
    didDrawPage: () => drawFooterBand(doc, docNo, profile),
    styles: {
      font: "helvetica",
      fontSize: 9.5,
      cellPadding: 3.5,
      lineColor: COLORS.border,
      lineWidth: 0.1,
      textColor: COLORS.charcoal,
    },
    headStyles: {
      fillColor: COLORS.orange,
      textColor: COLORS.white,
      fontStyle: "bold",
      halign: "left",
    },
    alternateRowStyles: {
      fillColor: COLORS.rowAlt,
    },
    columnStyles: {
      0: { cellWidth: "auto" },
      1: { cellWidth: 45, halign: "right" },
    },
  });

  cursorY = doc.lastAutoTable.finalY + 4;
  // Reserve enough vertical space for the totals stack so it never
  // gets sliced by a page break.
  cursorY = ensureSpace(doc, cursorY, 26);

  // ── Totals block (right-aligned) ───────────────────────────────────────────
  const subtotal = Number(totals.subtotal) || 0;
  const gst = gstRegistered
    ? (Number(totals.gst) || subtotal * GST_RATE)
    : 0;
  const total = Number(totals.total) || subtotal + gst;

  const totalsW = 75;
  const totalsX = PAGE.w - PAGE.margin - totalsW;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  setText(doc, COLORS.charcoal);

  if (gstRegistered) {
    doc.text("Subtotal (ex GST)", totalsX, cursorY);
    doc.text(fmtMoney(subtotal), PAGE.w - PAGE.margin, cursorY, { align: "right" });
    cursorY += 4.2;

    doc.text("GST (10%)", totalsX, cursorY);
    doc.text(fmtMoney(gst), PAGE.w - PAGE.margin, cursorY, { align: "right" });
    cursorY += 2.5;

    setDraw(doc, COLORS.border);
    doc.line(totalsX, cursorY, PAGE.w - PAGE.margin, cursorY);
    cursorY += 4;
  }

  // Charcoal total bar
  setFill(doc, COLORS.charcoal);
  doc.rect(totalsX - 2, cursorY - 3.5, totalsW + 2, 9, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11.5);
  setText(doc, COLORS.white);
  doc.text(gstRegistered ? "Total (inc GST)" : "Total", totalsX, cursorY + 2);
  doc.text(fmtMoney(total), PAGE.w - PAGE.margin - 1, cursorY + 2, {
    align: "right",
  });
  cursorY += 12;

  if (!gstRegistered) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8.5);
    setText(doc, COLORS.textMuted);
    doc.text(
      "GST not applicable — supplier not GST registered.",
      PAGE.w - PAGE.margin,
      cursorY,
      { align: "right" }
    );
    setText(doc, COLORS.charcoal);
    doc.setFont("helvetica", "normal");
    cursorY += 6;
  }

  // ── Quote notes (if any) ───────────────────────────────────────────────────
  if (notes) {
    cursorY = ensureSpace(doc, cursorY, 14);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    setText(doc, COLORS.charcoal);
    doc.text("Notes", PAGE.margin, cursorY);
    cursorY += 4;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.8);
    const wrapped = doc.splitTextToSize(notes, PAGE.w - PAGE.margin * 2);
    wrapped.forEach((w) => {
      doc.text(w, PAGE.margin, cursorY);
      cursorY += 4;
    });
    cursorY += 3;
  }

  // ── Payment terms box ──────────────────────────────────────────────────────
  const terms = isInvoice
    ? [
        `Payment due: ${fmtDate(computedDueDate)} (14 days from issue)`,
        invoiceStatus === "paid"
          ? "This invoice has been marked PAID — thank you."
          : `Pay by bank transfer using the details below, or contact ${
              profile.email || "us"
            } to arrange another payment method.`,
      ]
    : [
        "Payment terms: 14 days from acceptance",
        "Quote valid for 30 days from date of issue",
        `To accept this quote please reply to ${profile.email || "your tradie"} or contact us directly`,
      ];

  const paymentLines = [];
  if (isInvoice && payment && invoiceStatus !== "paid") {
    if (payment.accountName) paymentLines.push(`Account name: ${payment.accountName}`);
    if (payment.bsb) paymentLines.push(`BSB: ${payment.bsb}`);
    if (payment.accountNumber) paymentLines.push(`Account number: ${payment.accountNumber}`);
    if (payment.reference) paymentLines.push(`Reference: ${payment.reference}`);
  }
  const termsBoxLines = [...terms, ...paymentLines];

  const termsH = 7 + termsBoxLines.length * 4;
  cursorY = ensureSpace(doc, cursorY, termsH + 3);
  setFill(doc, COLORS.greyBox);
  doc.rect(PAGE.margin, cursorY, PAGE.w - PAGE.margin * 2, termsH, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  setText(doc, COLORS.charcoal);
  doc.text(isInvoice ? "PAYMENT DETAILS" : "PAYMENT TERMS", PAGE.margin + 4, cursorY + 4.5);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.6);
  let ty = cursorY + 9;
  termsBoxLines.forEach((t, i) => {
    if (isInvoice && i >= terms.length) {
      doc.setFont("helvetica", "bold");
      doc.text(t, PAGE.margin + 4, ty);
      doc.setFont("helvetica", "normal");
    } else {
      doc.text(t, PAGE.margin + 4, ty);
    }
    ty += 4;
  });
  cursorY += termsH + 4;

  // ── CTA button (kept before disclaimer so disclaimer sits as small tail) ──
  if (acceptUrl && !(isInvoice && invoiceStatus === "paid")) {
    const btnW = 72;
    const btnH = 10;
    const btnX = (PAGE.w - btnW) / 2;
    cursorY = ensureSpace(doc, cursorY, btnH + 4);

    setFill(doc, COLORS.orange);
    doc.roundedRect(btnX, cursorY, btnW, btnH, 2.5, 2.5, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    setText(doc, COLORS.white);
    doc.text(
      isInvoice ? "View Invoice Online" : "Accept Quote Online",
      btnX + btnW / 2,
      cursorY + btnH / 2 + 1,
      { align: "center", baseline: "middle" }
    );

    doc.link(btnX, cursorY, btnW, btnH, { url: acceptUrl });

    cursorY += btnH + 4;
    setText(doc, COLORS.charcoal);
    doc.setFont("helvetica", "normal");
  }

  // ── Disclaimer — small italic tail line(s) right above footer ────────────
  const disclaimerText =
    disclaimer ||
    (isInvoice
      ? "This invoice covers the agreed work as completed. Contact us within 7 days with any questions about line items or amounts."
      : "Note: This is a quote — final price may vary depending on site conditions, materials, or scope changes discovered during the job.");
  cursorY = ensureSpace(doc, cursorY, 8);
  doc.setFont("helvetica", "italic");
  doc.setFontSize(7.8);
  setText(doc, COLORS.textMuted);
  const dWrap = doc.splitTextToSize(disclaimerText, PAGE.w - PAGE.margin * 2);
  dWrap.forEach((w) => {
    doc.text(w, PAGE.margin, cursorY);
    cursorY += 3.4;
  });
  doc.setFont("helvetica", "normal");
  setText(doc, COLORS.charcoal);

  if (isInvoice && invoiceStatus === "paid") {
    drawPaidWatermark(doc);
  }

  drawFooterBand(doc, docNo, profile);

  stampPageNumbers(doc);

  // ── Output ─────────────────────────────────────────────────────────────────
  const blob = doc.output("blob");
  const dataUri = doc.output("datauristring");
  const base64 = dataUri.split(",")[1] || "";

  const customerName = safeName(form.fullName || lead?.customer);
  const fileLabel = isInvoice ? "Invoice" : "Quote";
  const filename = `RemoteQuote-${fileLabel}-${docNo}-${customerName}.pdf`;

  return { blob, base64, filename, quoteNumber: docNo, docNumber: docNo };
}

// Convenience wrapper — same options, kind="invoice" forced.
export async function generateInvoicePDF(opts) {
  return generateQuotePDF({ ...opts, kind: "invoice" });
}

// Draw the footer band at the bottom of the current page. Safe to call
// from inside autoTable.didDrawPage (per-page) AND from the end of the
// main generator (final tail page).
function drawFooterBand(doc, quoteNumber, profile) {
  const footerY = PAGE.h - PAGE.footerH;
  setFill(doc, COLORS.headerBand);
  doc.rect(0, footerY, PAGE.w, PAGE.footerH, "F");

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  setText(doc, COLORS.textMuted);

  const footerLeft = [
    profile.business_name || "",
    profile.abn ? `ABN ${profile.abn}` : "",
    profile.licence_number ? `Lic ${profile.licence_number}` : "",
    profile.has_insurance && profile.insurance_provider
      ? `Insured`
      : "",
  ]
    .filter(Boolean)
    .join("  ·  ");
  doc.text(footerLeft, PAGE.margin, footerY + 7);
  doc.text(quoteNumber, PAGE.w / 2, footerY + 7, { align: "center" });
  doc.text(
    "Powered by Remote Quote · remotequote.io",
    PAGE.w - PAGE.margin,
    footerY + 7,
    { align: "right" }
  );
}

// After all content is placed, walk through each page and stamp
// "Page X of Y" in the footer right corner.
function stampPageNumbers(doc) {
  const total = doc.getNumberOfPages();
  const footerY = PAGE.h - PAGE.footerH;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  setText(doc, COLORS.textMuted);
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.text(`Page ${i} of ${total}`, PAGE.w - PAGE.margin, footerY + 13, {
      align: "right",
    });
  }
}

// If the next block of neededH mm would overflow the footer band,
// emit a new page and reset the cursor to the top margin.
function ensureSpace(doc, cursorY, neededH) {
  const limit = PAGE.h - PAGE.footerH - 4;
  if (cursorY + neededH <= limit) return cursorY;
  doc.addPage();
  return PAGE.topMargin;
}

function drawBusinessNameText(doc, profile) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  setText(doc, COLORS.charcoal);
  doc.text(profile.business_name || profile.full_name || "Your Business", PAGE.margin, 22);
}

function drawStatusBadge(doc, status, rightX, y, isInvoice = false) {
  let label = "PENDING";
  let color = COLORS.amber;
  if (isInvoice) {
    if (status === "paid") {
      label = "PAID";
      color = COLORS.green;
    } else if (status === "sent" || status === "acknowledged") {
      label = "SENT";
      color = COLORS.orange;
    } else if (status === "overdue") {
      label = "OVERDUE";
      color = [220, 38, 38];
    }
  } else {
    if (status === "accepted") {
      label = "ACCEPTED";
      color = COLORS.green;
    } else if (status === "declined") {
      label = "DECLINED";
      color = [220, 38, 38];
    }
  }
  const w = 26;
  const h = 6;
  setFill(doc, color);
  doc.roundedRect(rightX - w, y, w, h, 1.5, 1.5, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  setText(doc, COLORS.white);
  doc.text(label, rightX - w / 2, y + 4.1, { align: "center" });
}

function drawPaidWatermark(doc) {
  doc.saveGraphicsState();
  try {
    const gs = new doc.GState({ opacity: 0.18 });
    doc.setGState(gs);
  } catch {
    // older jsPDF — skip transparency.
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(96);
  doc.setTextColor(34, 197, 94);
  doc.text("PAID", PAGE.w / 2, PAGE.h / 2 + 20, {
    align: "center",
    angle: 25,
  });
  doc.restoreGraphicsState();
  setText(doc, COLORS.charcoal);
}

// Helper: trigger a browser download for a blob.
export function downloadQuotePDF(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
